/**
 * Canonical microcopy: mirrors docs/branding.md §2.1. Change the doc first, then here.
 * Guarded by copy.test.ts (snapshot + tone checks).
 */
export const copy = {
  'auth.signup.title': 'Create your account',
  'auth.signup.success': "You're all set. We've sent your details to management for approval.",
  'auth.signup.emailInUse': 'An account with this email already exists. Try signing in instead.',
  'auth.signup.passwordMismatch': "These passwords don't match.",
  'auth.signup.confirmRequired': 'Please confirm your password.',
  'auth.login.title': 'Welcome back',
  'auth.login.error': "That email or password doesn't match our records. Please try again.",
  'auth.login.tooMany': 'Too many attempts. Please wait a few minutes and try again.',
  'auth.reset.sent':
    "If an account exists for that email, we've sent a reset link. It may take a minute to arrive.",
  'auth.logout.done': "You've signed out. See you soon.",
  'auth.verify.sent':
    "We've sent a welcome email to {email}. Tap the link in it to confirm your address.",
  'auth.verify.resend': 'Resend email',
  'auth.verify.resent': "We've sent it again. It may take a minute to arrive.",
  'auth.action.checking': 'Checking your link…',
  'auth.action.verified.title': 'Your email is confirmed',
  'auth.action.verified.body': 'Welcome to ShenandoahFirst. Thanks for confirming your address.',
  'auth.action.reset.title': 'Choose a new password',
  'auth.action.reset.for': 'For {email}',
  'auth.action.reset.done.title': 'Your password has been updated',
  'auth.action.reset.done.body': 'You can sign in with your new password now.',
  'auth.action.expired.title': 'This link has expired or was already used',
  'auth.action.expired.verify':
    'No problem. Sign in and we can send you a fresh link from your account page.',
  'auth.action.expired.verifySignedIn': 'No problem. We can send you a fresh link right away.',
  'auth.action.expired.reset':
    'Reset links work once and expire after a while. Request a new one and it will arrive in a minute.',
  'auth.action.invalid.title': "This link isn't valid",
  'auth.action.invalid.body':
    'It may be incomplete. Open the link from your email again, or request a new one.',

  'status.pending.title': 'Your account is awaiting admin approval',
  'status.pending.body':
    'Thanks for registering. Management will confirm your unit shortly, and this page will update by itself. You can safely close it.',
  'status.deactivated.title': 'Account deactivated, contact management',
  'status.deactivated.body':
    "Your access has been paused. If you think this is a mistake, please reach out to the building office. They'll be glad to help.",

  'ticket.new.title': 'Raise a ticket',
  'ticket.new.descriptionHint': "Tell us what's happening and where. Photos help us fix it faster.",
  'ticket.new.descriptionRequired': 'Please describe the issue.',
  'ticket.new.titleRequired': 'Please add a short title so we know what this is about.',
  'ticket.upload.progress': 'Uploading {n} of {total} photos…',
  'ticket.upload.tooMany': 'You can attach up to 5 photos.',
  'ticket.upload.badType': "That file type isn't supported. Please use JPG, PNG or WebP.",
  'ticket.upload.tooLarge':
    'This photo is larger than 5 MB, even after compressing. Please choose a smaller one.',
  'ticket.upload.failed': "Couldn't upload this photo. Check your connection and try again.",
  'ticket.created': "We've got your request. You'll see management's replies here.",
  'ticket.system.statusChanged': 'Status changed from {from} to {to} by {actor}',
  'ticket.status.closedNote': 'This ticket is closed. Need more help? Raise a new ticket anytime.',

  'chat.empty.ticket': 'No messages yet. Management will reply here.',
  'chat.empty.general': "Have a question for management? Send a message. We're here to help.",
  'chat.input.placeholder': 'Write a message…',
  'chat.sent': 'Sent',
  'chat.read': 'Read',
  'chat.offlineQueued': "You're offline. Your message will send when you're back online.",
  'chat.sendFailed': "Couldn't send your message. Tap to try again.",
  'chat.attach': 'Attach photos',
  'chat.photo.open': 'Open photo {n} of {total} from {sender}',
  'chat.photo.one': 'Sent a photo',
  'chat.photo.many': 'Sent {n} photos',

  'admin.approve.success': '{name} is approved for unit {unit}.',
  'admin.collision.title': 'Unit {unit} is already occupied',
  'admin.collision.body':
    'Unit {unit} is occupied by {name} since {date}. Deactivate them and approve {newName}?',
  'admin.collision.reassure': "{name}'s stay history will be kept.",
  'admin.collision.confirm': 'Deactivate & approve',
  'admin.collision.cancel': 'Cancel',
  'admin.reject.title': "Reject {name}'s registration?",
  'admin.reject.body':
    "They won't be able to use the portal. You can add a reason for your records (optional).",
  'admin.reject.success': 'Registration rejected.',
  'admin.deactivate.title': 'Deactivate {name}?',
  'admin.deactivate.body':
    'Their access ends now and unit {unit} becomes vacant. Their stay history will be kept.',
  'admin.deactivate.success': '{name} has been deactivated. Unit {unit} is now vacant.',
  'admin.email.verified': 'Email confirmed',
  'admin.email.unverified': 'Email not confirmed yet',
  'admin.notPending': 'This tenant is no longer pending. The list has been refreshed.',
  'admin.unit.created': 'Unit {unit} has been added.',
  'admin.unit.exists': 'Unit {unit} already exists.',
  'admin.unit.floorUpdated': 'Unit {unit} moved to {floorLabel}.',

  'empty.tickets.tenant':
    "No tickets yet. Something not working? Raise a ticket and we'll take it from there.",
  'empty.tickets.admin': 'No tickets match these filters.',
  'empty.approvals': "No one is waiting for approval. You're all caught up.",
  'empty.units': 'No units yet. Add your first unit to get started.',
  'empty.units.filtered': 'No units match these filters.',
  'empty.history': 'No past tenants for this unit yet.',
  'empty.chats': 'No conversations yet.',
  'loading.tickets': 'Fetching your tickets…',
  'loading.generic': 'Just a moment…',
  'net.offline': "You're offline. You can still browse, and we'll sync when you reconnect.",
  'net.online': "You're back online.",
  'error.generic': 'Something went wrong on our side. Please try again in a moment.',
  'error.permission':
    "You don't have access to this. If that seems wrong, please contact management.",
  'error.notFound': "We couldn't find that page.",

  'site.countdown.eyebrow': 'Coming soon',
  'site.countdown.title': 'Something calm is on its way',
  'site.countdown.body':
    "We're finishing ShenandoahFirst, your building's home for maintenance requests and messages with management. Thanks for your patience.",
  'site.countdown.opens': 'Opening {date} · {time} Pacific',
  'site.countdown.remaining': 'Opens in {time}',
  'site.done.title': "We're putting on the finishing touches",
  'site.done.body':
    "The countdown is over and we're nearly ready. This page will open by itself, so there's no need to refresh.",
  'site.maintenance.title': "We're making a few improvements",
  'site.maintenance.body':
    "ShenandoahFirst is having some planned maintenance. Your requests and messages are safe, and this page will update by itself when we're back.",
  'site.maintenance.until': "We'll be back in about {time}",
} as const;

export type CopyKey = keyof typeof copy;

/** Look up a message and fill `{placeholders}`. */
export function t(key: CopyKey, vars: Record<string, string | number> = {}): string {
  return copy[key].replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}
